import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  CalendarDays, ShieldCheck, Presentation, MessageSquare, TrendingUp, HelpCircle, Target, Eye,
  ChevronDown, ChevronUp, Search, Info, Users2, ClipboardList, GitCompareArrows,
} from "lucide-react";

const CLS = {
  "Published fact": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Analyst observation": "bg-slate-50 text-slate-600 border-slate-200",
  "Synthesised HCP perspective": "bg-teal-50 text-teal-700 border-teal-200",
  "AI-generated draft — expert validation required": "bg-violet-50 text-violet-700 border-violet-300 border-dashed",
};
const P_CLS = (p) => (p?.startsWith("Priority 1") ? "bg-red-50 text-red-700 border-red-200" : "bg-indigo-50 text-indigo-700 border-indigo-200");

const Sect = ({ icon: Icon, title, tone, children }) => (
  <div>
    <p className={`font-mono text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1 ${tone}`}><Icon className="w-3 h-3" />{title}</p>
    {children}
  </div>
);
const SHORT_LABEL = {
  "Potential session — requires confirmation": "Requires confirmation",
  "Analyst expectation — not a confirmed congress event": "Analyst expectation",
  "Published fact — illustrative": "Illustrative · verification pending",
  "Illustrative data point — source verification pending": "Illustrative · verification pending",
};
const Item = ({ text, label }) => (
  <li className="text-xs text-slate-700 leading-snug mb-1.5 break-words">• {text}
    {label && <span title={label} className="ml-1.5 align-middle inline-block max-w-full text-[9px] font-mono uppercase tracking-wide text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5">{SHORT_LABEL[label] || label}</span>}
  </li>
);

function PriorityBadge({ c }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative">
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }} data-testid={`priority-badge-${c.id}`}
        className={`text-[10px] font-semibold rounded-md border px-2 py-0.5 ${P_CLS(c.priority)}`}>{c.priority} <Info className="w-2.5 h-2.5 inline" /></button>
      {open && (
        <span className="absolute right-0 top-6 z-20 w-80 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-[11px] text-slate-600 font-normal block text-left" data-testid={`priority-rationale-${c.id}`}>
          <span className="font-mono text-[9px] uppercase tracking-wide text-slate-400 block mb-1">Priority rationale</span>{c.priority_rationale}
          {c.priority_detail && Object.entries(c.priority_detail).map(([l, v]) => (
            <span key={l} className="block mt-1"><span className="text-slate-400">{l}:</span> {v}</span>
          ))}
          <span className="block mt-1.5 text-amber-700">Priority classification does not imply the expected readout is confirmed.</span>
        </span>
      )}
    </span>
  );
}

function UpcomingCard({ c }) {
  const cov = c.coverage || {};
  return (
    <div data-testid={`congress-card-${c.id}`} className="bg-white border border-indigo-200 ring-1 ring-indigo-50 rounded-xl p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-heading text-base font-bold text-slate-900">{c.name}</h4>
          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><CalendarDays className="w-3 h-3" />{c.dates} · {c.location}</p>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <PriorityBadge c={c} />
          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`watchlist-flag-${c.id}`}>Future watchlist — outside evidence cut-off</Badge>
        </div>
      </div>
      <p className="text-[10.5px] text-slate-500 mt-1.5">Does not change the current assessment, confidence ratings or strategic conclusions. After completion and review, an administrator may initiate a congress-triggered refresh, a new cross-sectional assessment, or an addendum.</p>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {c.assets_competitors.map((a) => <Badge key={a} variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">{a}</Badge>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-3 mt-4 min-w-0">
        <Sect icon={Presentation} title="Sessions to confirm" tone="text-slate-500">
          <ul>{(c.session_items || []).map((s) => <Item key={s.text} text={s.text} label={s.classification} />)}</ul>
        </Sect>
        <Sect icon={Eye} title="Expected or potential readouts" tone="text-indigo-700">
          <ul>{(c.readout_items || []).map((s) => <Item key={s.text} text={s.text} label={s.classification} />)}</ul>
        </Sect>
      </div>
      <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3.5 mt-3" data-testid={`coverage-workspace-${c.id}`}>
        <p className="font-mono text-[9px] uppercase tracking-widest text-teal-700 mb-2 flex items-center gap-1"><ClipboardList className="w-3 h-3" />Prospective coverage workspace · authorised MarksMan personnel</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
          {[["Coverage status", cov.coverage_status], ["Assigned analyst", cov.assigned_analyst], ["Backup analyst", cov.backup_analyst],
            ["Embargo status", cov.embargo_status], ["Daily debrief", cov.debrief], ["Synthesis deadline", cov.synthesis_deadline]].map(([l, v]) => (
            <div key={l}><p className="text-[9px] font-mono uppercase tracking-wide text-slate-400">{l}</p><p className="text-[11px] text-slate-700 font-medium">{v || "—"}</p></div>
          ))}
        </div>
        <p className="text-[9px] font-mono uppercase tracking-wide text-slate-400 mt-2.5 mb-1">Permitted capture methods</p>
        <div className="flex flex-wrap gap-1.5">{(cov.capture_methods || []).map((m) => <span key={m} className="text-[10px] bg-white border border-slate-200 text-slate-600 rounded-full px-2 py-0.5">{m}</span>)}</div>
        <p className="text-[9px] font-mono uppercase tracking-wide text-slate-400 mt-2.5 mb-1">Priority intelligence questions</p>
        <ul>{(cov.intelligence_questions || []).map((q) => <li key={q} className="text-[11px] text-slate-600">• {q}</li>)}</ul>
        <p className="text-[10px] text-slate-500 mt-2">Recording or capture of restricted sessions is not permitted unless expressly authorised by the congress organiser and applicable rules.</p>
      </div>
    </div>
  );
}

function CapturedCard({ c, expanded, onToggle, onOpenDrawer }) {
  const top = [...c.data_readouts.slice(0, 2), ...(c.narrative_shifts[0] ? [c.narrative_shifts[0]] : [])].slice(0, 3);
  const na = c.narrative_analysis || {};
  return (
    <div data-testid={`congress-card-${c.id}`} className="bg-white border border-slate-200/80 rounded-xl shadow-sm">
      <button className="w-full text-left p-4" onClick={onToggle} data-testid={`congress-expand-${c.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h4 className="font-heading text-sm font-bold text-slate-900">{c.name}</h4>
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><CalendarDays className="w-3 h-3" />{c.dates} · {c.location}</p>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <PriorityBadge c={c} />
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`review-status-${c.id}`}>{c.review_status}</Badge>
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">In assessment — approved findings only</Badge>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {c.assets_competitors.slice(0, 3).map((a) => <Badge key={a} variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{a}</Badge>)}
          <span className="text-[10px] text-slate-400 ml-auto">{c.review_status} · {c.source_count} source records · <span className="text-amber-600">Illustrative demonstration data</span></span>
        </div>
        {!expanded && <ul className="mt-2">{top.map((t) => <li key={t} className="text-[11px] text-slate-600 truncate">• {t}</li>)}</ul>}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-4" data-testid={`congress-detail-${c.id}`}>
          {c.review_breakdown && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3" data-testid={`review-breakdown-${c.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5">Review breakdown — congress status is computed from its components</p>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {Object.entries(c.review_breakdown).map(([l, v]) => <span key={l} className="text-[10.5px] text-slate-600">{l}: <span className="font-semibold text-slate-800">{v}</span></span>)}
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">{c.inclusion_note} These statuses simulate the intended workflow on demonstration content and do not represent genuine verification.</p>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-3 min-w-0">
            <Sect icon={Eye} title="Reported data readouts" tone="text-indigo-700">
              <ul>{c.data_readouts.map((x) => <Item key={x} text={x} label="Illustrative data point — source verification pending" />)}</ul>
            </Sect>
            <Sect icon={MessageSquare} title="HCP reactions" tone="text-teal-700">
              <ul>{c.hcp_reactions.map((x) => <Item key={x} text={x} label="Synthesised HCP perspective" />)}</ul>
              {c.reaction_context && (
                <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5 mt-1.5" data-testid={`reaction-context-${c.id}`}>
                  <p className="font-mono text-[8.5px] uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1"><Users2 className="w-3 h-3" />Reaction calibration</p>
                  <p className="text-[10.5px] text-slate-600 leading-snug">
                    {c.reaction_context.collection_method} · {c.reaction_context.hcp_voices} HCP voices · {c.reaction_context.specialties} · {c.reaction_context.geography} · {c.reaction_context.distribution} · {c.reaction_context.spontaneous}.
                  </p>
                  <p className="text-[10.5px] text-slate-500 mt-1">Maturity: <span className="font-semibold">{c.reaction_context.maturity}</span> · Confidence: {c.reaction_context.confidence} · {c.reaction_context.review_status}</p>
                  <p className="text-[10px] text-amber-700 mt-1">Limitation: {c.reaction_context.limitation}</p>
                </div>
              )}
            </Sect>
            <Sect icon={TrendingUp} title={na.type || "Narrative"} tone="text-emerald-700">
              {na.type === "Narrative shift" ? (
                <div className="text-[11px] text-slate-600 space-y-1" data-testid={`narrative-shift-${c.id}`}>
                  <p><span className="font-mono text-[9px] uppercase text-slate-400 mr-1">Previous</span>{na.previous}</p>
                  <p><span className="font-mono text-[9px] uppercase text-slate-400 mr-1">Current</span>{na.current}</p>
                  <p className="text-[10px] text-slate-500">{na.period} · Segments: {na.segments} · {na.geography} · Evidence strength: {na.strength}</p>
                  <p className="text-[10px] text-slate-500">Prior sources: {na.prev_sources} · Current sources: {na.curr_sources}</p>
                  <p className="text-[10px] text-amber-700">Alternative explanation: {na.alternative}</p>
                  <p className="text-[10px] text-slate-400">{na.review}</p>
                </div>
              ) : (
                <div className="text-[11px] text-slate-600">
                  <p>• {na.current}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{na.note}</p>
                </div>
              )}
            </Sect>
            <Sect icon={HelpCircle} title="Emerging questions" tone="text-amber-700">
              <ul>{c.emerging_questions.map((x) => <Item key={x} text={x} label="Analyst observation" />)}</ul>
            </Sect>
          </div>
          <div className="bg-slate-900 rounded-lg p-3.5" data-testid={`analyst-obs-${c.id}`}>
            <p className="font-mono text-[9px] uppercase tracking-widest text-teal-400 mb-1.5">MarksMan analyst observation · illustrative</p>
            {c.analyst_structured ? (
              <div className="space-y-1">
                <p className="text-xs text-slate-300 leading-snug"><span className="font-mono text-[9px] uppercase text-slate-500 mr-1.5">Observed</span>{c.analyst_structured.observation}</p>
                <p className="text-[11px] text-slate-400"><span className="font-mono text-[9px] uppercase text-slate-500 mr-1.5">Interpretation</span>{c.analyst_structured.interpretation}</p>
                <p className="text-[11px] text-amber-400/80"><span className="font-mono text-[9px] uppercase text-slate-500 mr-1.5">Limitation</span>{c.analyst_structured.limitation}</p>
                <p className="text-[10px] text-slate-500 mt-1">Method: {c.analyst_structured.method} · {c.analyst_structured.review}</p>
              </div>
            ) : <p className="text-xs text-slate-300 leading-snug">{c.analyst_observations}</p>}
          </div>
          {c.interview_meta && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3" data-testid={`interview-meta-${c.id}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5">Commissioned research details</p>
              <p className="text-[10.5px] text-slate-600">{c.interview_meta.type} · {c.interview_meta.participants} participants · {c.interview_meta.segments} · {c.interview_meta.geography} · {c.interview_meta.date} · Consent: {c.interview_meta.consent} · Anonymised: {c.interview_meta.anonymised} · {c.interview_meta.attribution} · {c.interview_meta.review}</p>
            </div>
          )}
          <Sect icon={Target} title="Draft strategic implications" tone="text-slate-700">
            <div className="space-y-2">
              {(c.structured_implications || []).map((si) => (
                <div key={si.text} className="border border-slate-200/80 rounded-lg p-3">
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    <Badge variant="outline" className={`text-[9px] ${CLS["AI-generated draft — expert validation required"]}`}>{si.status}</Badge>
                    <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{si.function} · Confidence: {si.confidence}</Badge>
                  </div>
                  <p className="text-xs text-slate-700">{si.text}</p>
                  {si.statuses && (
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5">
                      {Object.entries(si.statuses).map(([l, v]) => <span key={l} className="text-[9.5px] text-slate-500">{l}: <span className="font-semibold">{v}</span></span>)}
                    </div>
                  )}
                  <p className="text-[10px] text-amber-700 mt-1">Limitation: {si.limitation}</p>
                </div>
              ))}
            </div>
          </Sect>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5">Supporting sources · {c.source_count} records</p>
            <div className="space-y-1">
              {c.sources.map((sc) => (
                <button key={sc.type} onClick={() => onOpenDrawer({
                  title: `${sc.type} — ${c.name}`,
                  rows: [["Source type", sc.type], ["Congress", c.name], ["Session / abstract / poster", "Illustrative demonstration record — identifiers withheld pending verification"],
                    ["Date", c.dates], ["Access classification", sc.access], ["Exact source location", "Recorded in analyst notes — available after source verification"],
                    ["Verification status", "Source verification pending (illustrative)"], ["Reviewer / review date", "Pending assignment"],
                    ["Related findings", `${c.data_readouts.length} reported readouts · ${c.hcp_reactions.length} HCP reactions`]],
                })} className="block text-left text-[11px] text-teal-700 hover:underline" data-testid={`source-link-${c.id}`}>
                  • {sc.type} <span className="text-slate-400">— {sc.access} · verification pending</span>
                </button>
              ))}
            </div>
            {c.insight_ids.length > 0 && (
              <p className="text-[10px] text-slate-400 mt-1.5">Linked HCP statements: {c.insight_ids.map((id) => `curated statement ${id}`).join(" · ")} — open in HCP Voice for full source detail</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [["overview", "Congress Overview"], ["upcoming", "Upcoming & Watchlist"], ["captured", "Captured Intelligence"], ["comparison", "Cross-Congress Comparison"]];
const TREND_CLS = { Strengthening: "text-emerald-700 bg-emerald-50 border-emerald-200", Weakening: "text-slate-500 bg-slate-50 border-slate-200", Unresolved: "text-amber-700 bg-amber-50 border-amber-200" };

export default function CongressIntel({ globalFilters = {} }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [prioF, setPrioF] = useState("all");
  const [showCompliance, setShowCompliance] = useState(false);
  const [drawer, setDrawer] = useState(null);

  useEffect(() => { api.get("/congress").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading congress intelligence…</div>;

  const gf = globalFilters || {};
  let congresses = data.congresses;
  if (gf.congress && gf.congress !== "all") congresses = congresses.filter((c) => c.name === gf.congress);
  const upcoming = congresses.filter((c) => c.status === "upcoming");
  const captured = congresses.filter((c) => c.status === "captured"
    && (!search || c.name.toLowerCase().includes(search.toLowerCase()))
    && (prioF === "all" || c.priority?.startsWith(prioF)));

  return (
    <div className="space-y-4" data-testid="congress-view">
      <div className="bg-white border border-slate-200/80 rounded-xl px-4 py-2.5 shadow-sm" data-testid="congress-compliance">
        <button className="w-full flex items-center gap-2 text-left" onClick={() => setShowCompliance(!showCompliance)} data-testid="compliance-toggle">
          <ShieldCheck className="w-4 h-4 text-teal-600 shrink-0" />
          <span className="text-xs font-semibold text-slate-700">Source & compliance statement</span>
          {showCompliance ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-auto" />}
        </button>
        {showCompliance && (
          <p className="text-xs text-slate-600 leading-snug mt-2">
            Congress intelligence is derived only from publicly available, legitimately accessed, licensed, commissioned or authorised sources. On-site observations are recorded by authorised MarksMan personnel in accordance with congress rules. HCP identities and statements are attributed only when legally and ethically appropriate. The platform does not imply unauthorised recording, restricted-content capture or access to private conversations.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`congress-tab-${id}`} onClick={() => setTab(id)}
            className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}>{label}</button>
        ))}
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-400">{data.assessment_version} · evidence cut-off {data.evidence_cutoff}</span>
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="congress-overview">
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm">
            <p className="font-mono text-[10px] uppercase tracking-widest text-indigo-700 mb-2">Upcoming priority congresses · future watchlist</p>
            {upcoming.map((c) => (
              <button key={c.id} onClick={() => setTab("upcoming")} className="w-full text-left border border-slate-100 rounded-lg p-3 mb-2 hover:border-teal-300 transition-colors">
                <p className="text-xs font-bold text-slate-900">{c.name} <span className="font-normal text-slate-500">· {c.dates}</span></p>
                <p className="text-[10.5px] text-slate-500 mt-0.5">{c.priority} · outside evidence cut-off — does not affect current assessment</p>
              </button>
            ))}
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm">
            <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-1">Most important captured findings</p>
            <p className="text-[9.5px] text-slate-400 mb-2">Selected by analysts for potential strategic relevance — importance rationale opens with each finding. Statuses simulate the intended workflow on demonstration content.</p>
            <ul>{captured.slice(0, 3).map((c) => (
              <li key={c.id} className="mb-2">
                <button className="text-left text-xs text-slate-700 hover:text-teal-700" onClick={() => { setTab("captured"); setExpanded(c.id); }} data-testid={`overview-finding-${c.id}`}>
                  • {c.name}: {c.data_readouts[0]}
                </button>
                <span className="flex flex-wrap gap-1.5 mt-0.5 ml-3">
                  <span className="text-[9px] font-mono uppercase text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5">Illustrative · verification pending</span>
                  <span className="text-[9px] font-mono uppercase text-amber-600 border border-amber-200 rounded-full px-1.5 py-0.5">{c.review_status}</span>
                  <span className="text-[9px] font-mono uppercase text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5">Confidence: Moderate · {c.source_count} sources</span>
                </span>
              </li>
            ))}</ul>
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber-700 mt-3 mb-2">Emerging clinical questions</p>
            <ul>{captured.slice(0, 3).map((c) => c.emerging_questions[0] && <Item key={c.id} text={c.emerging_questions[0]} label="Analyst observation" />)}</ul>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mb-2">Narrative & competitive developments</p>
            <ul>{captured.map((c) => c.narrative_shifts[0] && <Item key={c.id} text={`${c.name}: ${c.narrative_shifts[0]}`} label={c.narrative_analysis?.type} />)}</ul>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-700 mb-2">Draft strategic implications</p>
            {captured.flatMap((c) => (c.structured_implications || []).slice(0, 1).map((si) => (
              <div key={si.text} className="mb-2">
                <p className="text-xs text-slate-700">• {si.text}</p>
                <p className="text-[9.5px] text-violet-600 mt-0.5">{si.status} · {c.review_status} · {c.source_count} source records</p>
              </div>
            )))}
          </div>
        </div>
      )}

      {tab === "upcoming" && (
        <div className="space-y-4" data-testid="congress-upcoming">
          <p className="text-[11px] text-slate-500 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
            Future Watchlist — these congresses occur after the project evidence cut-off ({data.evidence_cutoff}) and are not included in the current assessment.</p>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{upcoming.map((c) => <UpcomingCard key={c.id} c={c} />)}</div>
        </div>
      )}

      {tab === "captured" && (
        <div className="space-y-3" data-testid="congress-captured">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-56">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="congress-search" placeholder="Search congress…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-8 text-xs rounded-full bg-white border-slate-200" />
            </div>
            <Select value={prioF} onValueChange={setPrioF}>
              <SelectTrigger data-testid="congress-priority-filter" className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-white"><span className="text-slate-400">Priority:</span>{prioF === "all" ? "All" : prioF}</SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Priority 1">Priority 1</SelectItem>
                <SelectItem value="Priority 2">Priority 2</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {captured.map((c) => (
            <CapturedCard key={c.id} c={c} expanded={expanded === c.id} onToggle={() => setExpanded(expanded === c.id ? null : c.id)} onOpenDrawer={setDrawer} />
          ))}
        </div>
      )}

      {tab === "comparison" && (
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm overflow-x-auto" data-testid="congress-comparison">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5"><GitCompareArrows className="w-3.5 h-3.5 text-teal-600" />Cross-congress comparison · captured congresses only</p>
          <p className="text-[10.5px] text-slate-500 mb-3">Longitudinal labels are used only where evidence exists from more than one congress time point. Illustrative demonstration data.</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 pr-4 font-mono text-[9px] uppercase tracking-widest text-slate-400">Dimension</th>
                {["ASCO 2025", "SABCS 2025", "ESMO Breast 2026"].map((n) => <th key={n} className="py-2 pr-4 font-semibold text-slate-700">{n}</th>)}
                <th className="py-2 font-mono text-[9px] uppercase tracking-widest text-slate-400">Trend</th>
              </tr>
            </thead>
            <tbody>
              {data.comparison.map((row) => (
                <tr key={row.dimension} className="border-b border-slate-100 align-top">
                  <td className="py-2.5 pr-4 font-semibold text-slate-800">{row.dimension}</td>
                  {["ASCO 2025", "SABCS 2025", "ESMO Breast 2026"].map((n) => <td key={n} className="py-2.5 pr-4 text-slate-600">{row.cells[n] || "—"}</td>)}
                  <td className="py-2.5"><button onClick={() => setDrawer({
                    title: `${row.dimension} — trend assessment`,
                    rows: [["Trend", row.trend], ["Baseline congress", row.baseline], ["Comparison congresses", "SABCS 2025, ESMO Breast 2026"],
                      ...["ASCO 2025", "SABCS 2025", "ESMO Breast 2026"].map((n) => [`Finding — ${n}`, row.cells[n] || "—"]),
                      ["Supporting sources", "Congress materials + analyst notes — verification pending (illustrative)"],
                      ["HCP segments", row.segments], ["Geography", row.geography], ["Assessment method", row.method],
                      ["Alternative explanation", row.alternative], ["Trend confidence", row.confidence], ["Review status", row.review]],
                  })} data-testid={`trend-${row.dimension.replace(/\W+/g, "-")}`}
                    className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap hover:ring-1 hover:ring-teal-300 ${TREND_CLS[row.trend] || "text-teal-700 bg-teal-50 border-teal-200"}`}>{row.trend}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="congress-drawer">
          {drawer && (
            <>
              <SheetHeader><SheetTitle className="font-heading text-base text-left">{drawer.title}</SheetTitle></SheetHeader>
              <div className="space-y-2.5 mt-4">
                {drawer.rows.map(([l, v]) => (
                  <div key={l}><p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{l}</p><p className="text-[11px] text-slate-700 leading-snug">{v}</p></div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
